import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import StoreNavigation from '../components/StoreNavigation';
import StoreFooter from '../components/StoreFooter';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useProducts } from '../context/ProductsContext';
import { useCart } from '../context/CartContext';
import { 
  ShoppingCart, 
  Truck, 
  RefreshCcw, 
  Shield, 
  CheckCircle, 
  ChevronLeft,
  Target,
  Activity,
  Droplet,
  Package,
  ArrowRight,
  Gift
} from 'lucide-react';
import { toast as sonnerToast } from '../components/ui/sonner';

const ProductDetail = () => {
  const { slug } = useParams();
  const { getProductBySlug, products } = useProducts();
  const product = getProductBySlug(slug);
  const { addToCart, getCartItemCount, cartItems } = useCart();

  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState(product?.colors?.[0] || null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-black text-[#004D51] mb-4">Product Not Found</h1>
          <Link to="/shop">
            <Button className="bg-[#DB5A14] hover:bg-[#F39C49] text-white font-bold">
              Back to Shop
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const showUpsell = () => {
    const candidates = (product.crossSells || [])
      .map(id => products.find(p => p.id === id))
      .filter(p => p && p.inStock && !cartItems.some(ci => ci.id === p.id));

    if (candidates.length === 0) {
      sonnerToast.success('Added to cart!', {
        description: `${product.name} (${selectedSize || 'One Size'}) is in your cart.`,
      });
      return;
    }

    sonnerToast.custom((id) => (
      <div className="w-[360px] max-w-[90vw] rounded-xl border-2 border-[#004D51] bg-white p-4 shadow-2xl" data-testid="upsell-toast">
        <p className="text-sm font-black text-[#004D51] mb-1">Added to cart!</p>
        <p className="text-xs text-[#545759] mb-3">Complete your kit — one-tap add:</p>
        <div className="space-y-2">
          {candidates.map((item) => (
            <div key={item.id} className="flex items-center gap-3">
              <img
                src={item.images[0]}
                alt={item.name}
                className="w-10 h-10 rounded object-cover border border-gray-200 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-[#004D51] truncate">{item.name}</p>
                <p className="text-xs text-[#545759]">${item.price.toFixed(2)} {item.currency}</p>
              </div>
              <button
                onClick={() => {
                  addToCart(item, item.sizes[0] || 'One Size', item.colors?.[0] || null, 1);
                  sonnerToast.success(`${item.name} added!`);
                  sonnerToast.dismiss(id);
                }}
                className="bg-[#DB5A14] hover:bg-[#F39C49] text-white text-xs font-black px-3 py-2 rounded-lg transition-colors flex-shrink-0"
                data-testid={`upsell-add-${item.id}`}
              >
                Add ${item.price.toFixed(2)}
              </button>
            </div>
          ))}
        </div>
      </div>
    ), { duration: 10000 });
  };

  const handleAddToCart = () => {
    if (!selectedSize && product.sizes.length > 1) {
      sonnerToast.error('Please select a size', {
        description: 'Choose your size before adding to cart.',
      });
      return;
    }

    addToCart(product, selectedSize || 'One Size', selectedColor, quantity);
    showUpsell();
  };

  const handleCrossSellAddToCart = (crossProduct) => {
    addToCart(crossProduct, crossProduct.sizes[0] || 'One Size', crossProduct.colors?.[0] || null, 1);
    sonnerToast.success('Added to cart!', {
      description: `${crossProduct.name} has been added to your cart.`,
    });
  };

  const crossSellProducts = product.crossSells
    ?.map(id => products.find(p => p.id === id))
    .filter(Boolean) || [];

  const featureIcons = {
    target: Target,
    shield: Shield,
    activity: Activity,
    droplet: Droplet,
    package: Package,
    move: Activity,
    wind: Activity,
  };

  return (
    <div className="min-h-screen bg-white">
      <StoreNavigation cartItemCount={getCartItemCount()} />

      <div className="pt-24 pb-16">
        <div className="container mx-auto px-6">
          {/* Breadcrumb */}
          <nav className="mb-8 flex items-center text-sm">
            <Link to="/shop" className="text-[#545759] hover:text-[#DB5A14] font-medium flex items-center">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Shop
            </Link>
            <span className="mx-2 text-[#545759]">/</span>
            <span className="text-[#004D51] font-bold">{product.name}</span>
          </nav>

          {/* Product Main Section */}
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            {/* Left: Image Gallery */}
            <div>
              {/* Main Image */}
              <div className="relative aspect-square bg-gray-100 rounded-2xl overflow-hidden mb-4 border-4 border-[#004D51]">
                <img
                  src={product.images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                {!product.inStock && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <div className="bg-white px-8 py-4 rounded-lg">
                      <p className="text-2xl font-black text-[#004D51]">OUT OF STOCK</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Thumbnail Gallery */}
              <div className="grid grid-cols-5 gap-2">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index
                        ? 'border-[#DB5A14] ring-2 ring-[#DB5A14]'
                        : 'border-gray-300 hover:border-[#004D51]'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} view ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Right: Product Info */}
            <div>
              <h1 className="text-4xl md:text-5xl font-black text-[#004D51] mb-4 leading-tight">
                {product.name}
              </h1>
              
              <p className="text-xl text-[#545759] mb-6 font-medium">
                {product.tagline}
              </p>

              {/* Price */}
              <div className="mb-8">
                <div className="text-4xl font-black text-[#004D51] mb-2">
                  ${product.price.toFixed(2)} {product.currency}
                </div>
                <p className="text-sm text-[#545759] font-medium">
                  {product.inStock ? '✓ In Stock - Ships within 2-3 business days' : '✗ Currently Out of Stock'}
                </p>
              </div>

              {/* Colourway (single - we only offer one colourway) */}
              {product.colors && product.colors.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-black text-[#004D51] mb-3 uppercase tracking-wide">
                    Colourway: {product.colors[0].name}
                  </label>
                  <div
                    className="w-12 h-12 rounded-lg border-4 border-[#004D51]"
                    style={{ background: `linear-gradient(135deg, ${product.colors[0].hex} 50%, ${product.colors[0].hex2 || product.colors[0].hex} 50%)` }}
                    title={product.colors[0].name}
                    data-testid="product-colourway-swatch"
                  />
                </div>
              )}

              {/* Size Selector */}
              {product.sizes && product.sizes.length > 1 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-black text-[#004D51] uppercase tracking-wide">
                      Size: {selectedSize || 'Select Size'}
                    </label>
                    <Link to="/fit" className="text-sm text-[#DB5A14] hover:text-[#F39C49] font-bold">
                      Size Guide →
                    </Link>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {product.sizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`py-3 px-4 rounded-lg border-2 font-black transition-all ${
                          selectedSize === size
                            ? 'border-[#DB5A14] bg-[#DB5A14] text-white'
                            : 'border-[#004D51] text-[#004D51] hover:bg-[#F3CEB2]'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity */}
              <div className="mb-8">
                <label className="block text-sm font-black text-[#004D51] mb-3 uppercase tracking-wide">
                  Quantity
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 bg-gray-200 hover:bg-[#F3CEB2] rounded-lg font-black text-xl transition-all"
                  >
                    -
                  </button>
                  <span className="text-2xl font-black text-[#004D51] min-w-12 text-center">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 bg-gray-200 hover:bg-[#F3CEB2] rounded-lg font-black text-xl transition-all"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <div className="space-y-4 mb-8">
                <Button
                  onClick={handleAddToCart}
                  disabled={!product.inStock}
                  className="w-full h-16 bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black text-lg rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  <ShoppingCart className="w-5 h-5 mr-3" />
                  {product.inStock ? 'Add to Cart' : 'Out of Stock'}
                </Button>
                
                {product.inStock && (
                  <Button className="w-full h-16 bg-[#004D51] hover:bg-[#003338] text-white font-black text-lg rounded-lg transition-all">
                    Buy Now - Express Checkout
                  </Button>
                )}
              </div>

              {/* Upgrade to Bundle Suggestion */}
              {product.upgradeBundle && (
                <Link
                  to={`/product/${product.upgradeBundle.slug}`}
                  className="block mb-8 group"
                  data-testid="upgrade-bundle-banner"
                >
                  <div className="flex items-center gap-4 rounded-xl border-2 border-dashed border-[#DB5A14] bg-[#F3CEB2]/20 p-5 transition-all group-hover:bg-[#F3CEB2]/40">
                    <div className="w-12 h-12 flex-shrink-0 bg-[#DB5A14] rounded-lg flex items-center justify-center">
                      <Gift className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-black text-[#004D51] leading-tight">
                        {product.upgradeBundle.title}
                      </p>
                      <p className="text-sm text-[#545759]">
                        {product.upgradeBundle.description}
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-[#DB5A14] flex-shrink-0 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              )}

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4 pb-8 border-b-2 border-gray-200">
                <div className="text-center">
                  <Truck className="w-6 h-6 mx-auto mb-2 text-[#DB5A14]" />
                  {product.freeShipping ? (
                    <>
                      <p className="text-xs font-bold text-[#545759]">Free Shipping</p>
                      <p className="text-xs text-[#545759]">On this bundle</p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs font-bold text-[#545759]">Fast Dispatch</p>
                      <p className="text-xs text-[#545759]">Ships in 2-3 days</p>
                    </>
                  )}
                </div>
                <div className="text-center">
                  <RefreshCcw className="w-6 h-6 mx-auto mb-2 text-[#DB5A14]" />
                  <p className="text-xs font-bold text-[#545759]">Easy Returns</p>
                  <p className="text-xs text-[#545759]">30-day policy</p>
                </div>
                <div className="text-center">
                  <Shield className="w-6 h-6 mx-auto mb-2 text-[#DB5A14]" />
                  <p className="text-xs font-bold text-[#545759]">Quality Promise</p>
                  <p className="text-xs text-[#545759]">Premium materials</p>
                </div>
              </div>

              {/* Short Description */}
              <div className="mt-8">
                <p className="text-[#545759] leading-relaxed">
                  {product.shortDescription}
                </p>
              </div>
            </div>
          </div>

          {/* Key Features Grid */}
          {product.features?.length > 0 && (
          <section className="mb-16">
            <h2 className="text-4xl font-black text-[#004D51] mb-8 text-center">
              Key Features
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {product.features.map((feature, index) => {
                const Icon = featureIcons[feature.icon] || CheckCircle;
                return (
                  <div
                    key={index}
                    className="bg-white border-4 border-[#004D51] rounded-xl p-6 hover:shadow-xl transition-all transform hover:-translate-y-1"
                  >
                    <div className="w-12 h-12 bg-[#DB5A14] rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-black text-[#004D51] mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-[#545759]">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>
          )}

          {/* Detailed Information - Accordion */}
          <section className="mb-16 max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="description" className="border-4 border-[#004D51] rounded-xl px-6 bg-white shadow-lg">
                <AccordionTrigger className="text-lg font-black text-[#004D51] hover:text-[#DB5A14] py-4">
                  Full Description
                </AccordionTrigger>
                <AccordionContent className="text-[#545759] pb-4">
                  <div className="whitespace-pre-line leading-relaxed">
                    {product.detailedDescription || product.shortDescription}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {product.specifications && (
                <AccordionItem value="specs" className="border-4 border-[#DB5A14] rounded-xl px-6 bg-white shadow-lg">
                  <AccordionTrigger className="text-lg font-black text-[#004D51] hover:text-[#DB5A14] py-4">
                    Technical Specifications
                  </AccordionTrigger>
                  <AccordionContent className="text-[#545759] pb-4">
                    <dl className="space-y-3">
                      {Object.entries(product.specifications).map(([key, value]) => (
                        <div key={key} className="flex border-b border-gray-200 pb-2">
                          <dt className="font-bold text-[#004D51] w-1/3 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}:
                          </dt>
                          <dd className="w-2/3">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </AccordionContent>
                </AccordionItem>
              )}

              {product.fitNotes && (
                <AccordionItem value="fit" className="border-4 border-[#F39C49] rounded-xl px-6 bg-white shadow-lg">
                  <AccordionTrigger className="text-lg font-black text-[#004D51] hover:text-[#DB5A14] py-4">
                    Fit & Sizing
                  </AccordionTrigger>
                  <AccordionContent className="text-[#545759] pb-4">
                    <ul className="space-y-2">
                      {product.fitNotes.map((note, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-[#DB5A14] mr-2 flex-shrink-0 mt-0.5" />
                          <span>{note}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/fit" className="inline-block mt-4">
                      <Button className="bg-[#004D51] hover:bg-[#003338] text-white font-bold">
                        View Complete Size Guide
                      </Button>
                    </Link>
                  </AccordionContent>
                </AccordionItem>
              )}

              {product.care && (
                <AccordionItem value="care" className="border-4 border-[#004D51] rounded-xl px-6 bg-white shadow-lg">
                  <AccordionTrigger className="text-lg font-black text-[#004D51] hover:text-[#DB5A14] py-4">
                    Care & Maintenance
                  </AccordionTrigger>
                  <AccordionContent className="text-[#545759] pb-4">
                    <ul className="space-y-2">
                      {product.care.map((instruction, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-[#DB5A14] mr-2 flex-shrink-0 mt-0.5" />
                          <span>{instruction}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              )}

              <AccordionItem value="shipping" className="border-4 border-[#DB5A14] rounded-xl px-6 bg-white shadow-lg">
                <AccordionTrigger className="text-lg font-black text-[#004D51] hover:text-[#DB5A14] py-4">
                  Shipping & Returns
                </AccordionTrigger>
                <AccordionContent className="text-[#545759] pb-4">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-bold text-[#004D51] mb-2">Shipping:</h4>
                      <ul className="space-y-1 ml-4">
                        <li>• Standard shipping: 5-7 business days</li>
                        <li>• Express shipping: 2-3 business days</li>
                        <li>• Fast shipping Australia-wide</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#004D51] mb-2">Returns:</h4>
                      <ul className="space-y-1 ml-4">
                        <li>• 30-day return policy</li>
                        <li>• Items must be unworn and in original packaging</li>
                        <li>• Free returns on Australian orders</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </section>

          {/* Cross-Sell Products */}
          {crossSellProducts.length > 0 && (
            <section className="mb-16">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-black text-[#004D51] mb-4">
                  Complete Your <span className="text-[#DB5A14]">Kit</span>
                </h2>
                <p className="text-xl text-[#545759]">
                  Accessories to extend the life and performance of your suit
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                {crossSellProducts.map((crossProduct) => (
                  <ProductCard key={crossProduct.id} product={crossProduct} onAddToCart={handleCrossSellAddToCart} />
                ))}
              </div>
            </section>
          )}

          {/* CTA Banner */}
          <section className="bg-gradient-to-br from-[#DB5A14] to-[#F39C49] rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl font-black mb-4">Questions About This Product?</h3>
            <p className="text-xl mb-8 text-white/90">
              We&apos;re here to help you find the perfect fit and answer any questions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/fit">
                <Button className="bg-white text-[#DB5A14] hover:bg-[#F3CEB2] font-black px-8 py-6 text-lg">
                  View Fit Guide
                </Button>
              </Link>
              <Link to="/support">
                <Button className="bg-white/10 hover:bg-white/20 text-white font-black px-8 py-6 text-lg border-2 border-white">
                  Contact Support
                </Button>
              </Link>
            </div>
          </section>
        </div>
      </div>

      <StoreFooter />
    </div>
  );
};

export default ProductDetail;
